// React and Router imports
import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom"
import { auth } from "../../firebase.js";

// Components
import Sidebar from "../shared/Sidebar.js";
import { useContextMenu } from "../shared/ContextMenu";
import { useQuickLinks } from "../../contexts/QuickLinksContext";

// Styles
import "../../css/DocumentationMainPage.css"
import "../../css/Create.css"
import "../../css/Help.css"
import "../../css/Document.css"
import "../../css/HomePage.css"

function Help() {

        const [email, setEmail] = useState("")
        const [phoneNumber, setPhoneNumber] = useState("+1")
        const [subject, setSubject] = useState("")
        const [message, setMessage] = useState("")
        const [firstName, setFirstName] = useState("")
        const { enhancedRightClickEnabled, emailVerified, emailVerifiedLoading } = useQuickLinks();
        const [isSubmitting, setIsSubmitting] = useState(false);
        const [submitMessage, setSubmitMessage] = useState("");
        const [showSuccessMessage, setShowSuccessMessage] = useState(false);
        const navigate = useNavigate();
        const [authWarning, setAuthWarning] = useState(null);

        const handleSupportButtonClick = () => { navigate("/help/support-ticket") }

        // Theme cleanup on unmount


        // Show warning modal if user is not authenticated
        useEffect(() => {
                const unsubscribe = auth.onAuthStateChanged((user) => {
                        if (!user) setAuthWarning('unauthenticated');
                });
                return () => unsubscribe();
        }, []);

        // Show warning modal if user's email is not verified
        useEffect(() => {
                if (emailVerifiedLoading) return;
                const user = auth.currentUser;
                if (!user) return;
                setAuthWarning(emailVerified ? null : 'unverified');
        }, [emailVerified, emailVerifiedLoading]);


        const loggedOutOptions = [
                {
                        label: "Home",
                        icon: <i className="fas fa-home"></i>,
                        url: "/",
                        onClick: () => navigate("/")
                },
                {
                        label: "Join Discord",
                        icon: <i className="fab fa-discord"></i>,
                        url: "https://discord.gg/DYkESHwgVA",
                        onClick: () => window.open("https://discord.gg/DYkESHwgVA", "_blank", "noopener,noreferrer")
                }
        ];

        const menuOptions = loggedOutOptions
        const { showContextMenu, ContextMenuComponent } = useContextMenu(menuOptions);

        const handleSubmit = async () => {
                // Check authentication before anything else
                if (!auth.currentUser) {
                        setSubmitMessage("You must be signed in to send a message. If you're unable to create an account, reach out to us on Discord (discord.gg/DYkESHwgVA).");
                        setShowSuccessMessage(false);
                        return;
                }

                // Validate required fields
                if (!firstName || !email || !subject || !message) {
                        setSubmitMessage("Please fill out all required fields.");
                        setShowSuccessMessage(false);
                        return;
                }

                // Basic email validation
                const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
                if (!emailRegex.test(email)) {
                        setSubmitMessage("Please enter a valid email address.");
                        setShowSuccessMessage(false);
                        return;
                }

                setIsSubmitting(true);
                setSubmitMessage("");

                const data = {
                        email: email,
                        phoneNumber: phoneNumber,
                        subject: subject,
                        message: message,
                        firstName: firstName,
                }

                try {
                        const token = await auth.currentUser.getIdToken();
                        const response = await fetch("https://bluebirddocumentationadmin.pythonanywhere.com/support/", {
                                method: "POST",
                                headers: {
                                        "Content-Type": "application/json",
                                        "Authorization": `${token}`
                                },
                                body: JSON.stringify(data),
                        })

                        if (response.ok) {
                                console.log("Successfully submitted general contact form.")
                                
                                // Clear the form
                                setFirstName("");
                                setEmail("");
                                setPhoneNumber("+1");
                                setSubject("");
                                setMessage("");
                                
                                // Show success message
                                setSubmitMessage("Message sent successfully! We'll get back to you within 2 business days.");
                                setShowSuccessMessage(true);
                                
                                // Hide success message after 5 seconds
                                setTimeout(() => {
                                        setSubmitMessage("");
                                        setShowSuccessMessage(false);
                                }, 5000);
                                
                        } else {
                                console.log("Failed to send general contact form data.")
                                setSubmitMessage("Failed to send your message. Please try again.");
                                setShowSuccessMessage(false);
                        }
                } catch (error) {
                        console.error("Error in submitting general contact form data: ", error)
                        setSubmitMessage("An error occurred while sending your message. Please try again.");
                        setShowSuccessMessage(false);
                } finally {
                        setIsSubmitting(false);
                }
        }

        return (
                <div 
                    className="DocumentationMainPage__div"
                    onContextMenu={enhancedRightClickEnabled ? (e) => {
                            e.preventDefault();
                            showContextMenu(e.clientX, e.clientY);
                    } : undefined}
                >
                        <Sidebar />

                        <div className="DocumentationMainPage__main-content">

                                <div className="Document__div">

                                        <p>We can't thank you enough for giving our product a shot; we hope we can meet all your documentation needs!</p>

                                        <p>
                                                <strong>If you have any questions, comments, and/or concerns</strong>, please feel free to use the form below to send us 
                                                an email. We're a very small team, but we'll try our best to get back to you within 2 business days.
                                        </p>

                                        <p>
                                                <strong>If you would like to submit a support ticket</strong>, please click the button below.
                                        </p>

                                        <button onClick={handleSupportButtonClick} className="Help__support-ticket-button">Submit Support Ticket</button>

                                        <div className="Create__form-group-helper-text">
                                                <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                                                <p className="Create__helper-text">
                                                        If you're encountering any technical issues while trying to use our app, we recommend submitting a ticket; rather than sending an
                                                        email via the form below. We try our best to log all errors, so there is a chance we're already working on an error you've encountered, but
                                                        we still highly recommend submitting a detailed ticket.
                                                </p>
                                        </div>

                                        <h2 style={{ marginTop: "50px" }}>Data Deletion Requests</h2>

                                        <p>
                                                If you use the <strong>Bluebird Tasks</strong> Android app (package <code>com.bluebird.tasks</code>),
                                                the <strong>Bluebird Documentation</strong> web app, or any other product in the
                                                <strong> Bluebird Suite</strong> (all published by <strong>Bluebird Documentation</strong>),
                                                you can request the deletion of part of your data without deleting your entire account.
                                        </p>

                                        <p>
                                                <strong>If you'd like to delete your entire Bluebird account</strong> (and every piece of data
                                                tied to it across the Bluebird Suite), use the dedicated deletion page instead:{" "}
                                                <a href="/delete-account">bluebird-documentation.com/delete-account</a>.
                                        </p>

                                        <p><strong>How to request a partial data deletion:</strong></p>
                                        <ol style={{ paddingLeft: 20, lineHeight: 1.7 }}>
                                                <li>Sign in with the Bluebird account that owns the data.</li>
                                                <li>Use the <strong>General Contact Form</strong> below.</li>
                                                <li>Set the <em>Subject</em> to <strong>"Data Deletion Request"</strong>.</li>
                                                <li>In the <em>Message</em>, list exactly which items you want removed (document names, workspace names, notebook titles, task boards, audio transcriptions, attachments, etc.).</li>
                                                <li>We'll review and confirm the deletion by email, typically within 2 business days.</li>
                                        </ol>

                                        <p style={{ marginTop: 20 }}><strong>What we can delete on request:</strong></p>
                                        <ul style={{ paddingLeft: 20, lineHeight: 1.7 }}>
                                                <li>Specific personal documents, pages, walkthroughs, and their snapshots</li>
                                                <li>Specific notebooks, drawings, saved strokes, and document markups</li>
                                                <li>Specific task boards, columns, tasks, and task comments</li>
                                                <li>Team workspaces you own (and the content within them)</li>
                                                <li>Specific audio transcriptions, focus sessions, planner entries, or notifications</li>
                                                <li>Uploaded attachments</li>
                                                <li>Your social profile links and quick-navigation links on your account</li>
                                        </ul>

                                        <p style={{ marginTop: 20 }}><strong>What we keep, and for how long:</strong></p>
                                        <ul style={{ paddingLeft: 20, lineHeight: 1.7 }}>
                                                <li><strong>Billing and payment records</strong> (Stripe customer ID, subscription history, invoices) — these live in our payment processor, <strong>Stripe</strong>, and follow Stripe's own data retention policy: typically <strong>5 or more years</strong> from the end of the business relationship or the date of the last transaction, whichever is later. We have no ability to shorten that window. See <a href="https://support.stripe.com/questions/data-retention-policy" target="_blank" rel="noopener noreferrer">Stripe's data retention policy</a> for full details.</li>
                                                <li><strong>Support ticket history and contact-form submissions</strong> — retained for up to <strong>2 years</strong> for audit and quality-assurance purposes.</li>
                                                <li><strong>Server error logs and access logs</strong> — these are managed by our hosting provider, <strong>PythonAnywhere</strong>. Logs are rotated daily and automatically deleted <strong>7 days</strong> after rotation, per <a href="https://www.pythonanywhere.com/forums/topic/1328/" target="_blank" rel="noopener noreferrer">PythonAnywhere's log rotation policy</a>. We do not archive them ourselves.</li>
                                                <li><strong>Aggregated, anonymized analytics</strong> (no longer tied to your account) — retained indefinitely.</li>
                                                <li><strong>Content shared with other users</strong> (comments left on someone else's document, contributions to workspaces you don't own) — the original owner controls those records; we cannot unilaterally remove them on your behalf, but the comment author attribution will be removed if you fully delete your account.</li>
                                        </ul>

                                        <p style={{ marginTop: 20 }}>
                                                If you have any questions about how a specific category of data is handled, include them in your
                                                message and we'll respond before taking any irreversible action.
                                        </p>

                                        <h2 style={{ marginTop: "50px" }}>General Contact Form</h2>

                                        <div className="Create__form-group">
                                                <label>First Name: </label>
                                                <input
                                                        type="text"
                                                        id="firstName"
                                                        name="email"
                                                        className="Create__input-short-field"
                                                        value={firstName}
                                                        onChange={(e) => setFirstName(e.target.value)}
                                                        maxLength={100}
                                                        required
                                                />
                                        </div>

                                        <div className="Create__form-group">
                                                <label>Email: </label>
                                                <input
                                                        type="email"
                                                        id="email"
                                                        name="email"
                                                        className="Create__input-short-field"
                                                        value={email}
                                                        onChange={(e) => setEmail(e.target.value)}
                                                        required
                                                />
                                        </div>

                                        <div className="Create__form-group">
                                                <label>Phone Number: </label>
                                                <input
                                                        type="tel"
                                                        name="phoneNumber"
                                                        value={phoneNumber}
                                                        className="Create__input-short-field"
                                                        onChange={(e) => {
                                                                const value = e.target.value;
                                                                if (/^\+?[0-9\s]*$/.test(value)) {
                                                                        setPhoneNumber(value);
                                                                }
                                                        }}
                                                        placeholder="+1 (123) 456-7890"
                                                        pattern="^\+\d{1,4}\s?\(?\d{1,4}\)?\s?\d{1,9}(\s?\d{1,9})?$"
                                                        title="Phone number must start with a '+' followed by the country code and numbers."
                                                />
                                        </div>
                                        <div className="Create__form-group-helper-text">
                                                <i className="fas fa-info-circle Create__info-icon" title="Information"></i>
                                                <p className="Create__helper-text">
                                                        We will never call or text you without emailing you first. This field is optional, and it is 
                                                        only to set up a phone call if needed.
                                                </p>
                                        </div>

                                        <div className="Create__form-group">
                                                <label>Subject: </label>
                                                <input
                                                        type="text"
                                                        id="subject"
                                                        name="subject"
                                                        className="Create__input-full-field"
                                                        value={subject}
                                                        onChange={(e) => setSubject(e.target.value)}
                                                        maxLength={150}
                                                        required
                                                />
                                        </div>

                                        <div className="Create__form-group-text-area">
                                                <label>Message: </label>
                                                <textarea
                                                        className="Create__input-text-area"
                                                        rows="10"
                                                        id="message"
                                                        name="message"
                                                        value={message}
                                                        onChange={(e) => setMessage(e.target.value)}
                                                        required
                                                ></textarea>
                                        </div>

                                        <button 
                                onClick={handleSubmit} 
                                className="Help__submit-button Help__shorter-margin-top"
                                disabled={isSubmitting}
                        >
                                {isSubmitting ? 'Submitting...' : 'Submit'}
                        </button>
                        
                        {submitMessage && (
                                <div className={`submit-message ${showSuccessMessage ? 'success' : 'error'}`}>
                                        {submitMessage}
                                </div>
                        )}

                                </div>

                        </div>

                        {enhancedRightClickEnabled && ContextMenuComponent}

                        {authWarning && (
                                <div style={{
                                        position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
                                        background: 'rgba(0,0,0,0.75)', zIndex: 9999,
                                        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'
                                }}>
                                        <div style={{
                                                background: '#111827', border: '1px solid rgba(91,146,229,0.35)',
                                                borderRadius: '12px', padding: '32px', maxWidth: '440px',
                                                width: '100%', textAlign: 'center'
                                        }}>
                                                {authWarning === 'unverified' && (
                                                        <div style={{ fontSize: '2.5rem', marginBottom: '12px' }}>✉️</div>
                                                )}
                                                <h3 style={{ color: '#5B92E5', marginBottom: '12px' }}>
                                                        {authWarning === 'unauthenticated' ? 'Sign In Required' : 'Email Verification Required'}
                                                </h3>
                                                <p style={{ color: '#cbd5e1', marginBottom: '24px', lineHeight: '1.6' }}>
                                                        {authWarning === 'unauthenticated'
                                                                ? "You need to be signed in to send us a message. If you're unable to create an account, reach out to us on Discord."
                                                                : "Your email address needs to be verified before you can send us a message. If you're having trouble, reach out to us on Discord."}
                                                </p>
                                                <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
                                                        {authWarning === 'unauthenticated' ? (
                                                                <>
                                                                        <button onClick={() => navigate('/login')} style={{ background: '#5B92E5', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                                                                                Log In
                                                                        </button>
                                                                        <button onClick={() => navigate('/register')} style={{ background: 'transparent', color: '#5B92E5', border: '1px solid #5B92E5', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                                                                                Create Account
                                                                        </button>
                                                                </>
                                                        ) : (
                                                                <button onClick={() => navigate('/verify-email')} style={{ background: '#5B92E5', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem' }}>
                                                                        Verify Email
                                                                </button>
                                                        )}
                                                        <button onClick={() => window.open('https://discord.gg/DYkESHwgVA', '_blank', 'noopener,noreferrer')} style={{ background: '#5865F2', color: 'white', border: 'none', borderRadius: '8px', padding: '10px 20px', cursor: 'pointer', fontWeight: '600', fontSize: '1rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px' }}>
                                                                <i className="fab fa-discord"></i> Join Discord
                                                        </button>
                                                        <button onClick={() => setAuthWarning(null)} style={{ background: 'transparent', color: '#64748b', border: 'none', padding: '8px', cursor: 'pointer', fontSize: '0.875rem' }}>
                                                                Dismiss
                                                        </button>
                                                </div>
                                        </div>
                                </div>
                        )}

                </div>
        )

}

export default Help